import { ItemStatus } from "@focus/focusUI";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { catchError, map, mergeMap, of } from "rxjs";
import type {
  Organization,
  User,
  UserControllerState,
  UserRequestKey,
} from "../types/users";
import { createAsyncReducers } from "./helpers";

const baseURL = "/api/v1/access";

const initialState: UserControllerState = {
  organizations: [],
  roleGroups: [],
  users: [],
  requests: {
    getUser: { status: ItemStatus.READY, error: null },
    getUserMe: { status: ItemStatus.READY, error: null },
    getUsers: { status: ItemStatus.READY, error: null },
    addUser: { status: ItemStatus.READY, error: null },
    updateUser: { status: ItemStatus.READY, error: null },
    deleteUser: { status: ItemStatus.READY, error: null },
    addOrganization: { status: ItemStatus.READY, error: null },
    deleteOrganization: { status: ItemStatus.READY, error: null },
    resetPassword: { status: ItemStatus.READY, error: null },
    restoreTotp: { status: ItemStatus.READY, error: null },
    enableUser: { status: ItemStatus.READY, error: null },
    disableUser: { status: ItemStatus.READY, error: null },
  },
};

export const userControllerMessages = {
  user: {
    createSuccess: "User added successfully.",
    updateSuccess: "USer updated successfully.",
    deleteSuccess: "User deleted successfully.",
    usersLoadError: "Could not load users.",
    userInfoError: "Could not load user info.",
    passwordResetSuccess: "Password reset successfully.",
    totpRestoreSuccess: "TOTP restore successfully.",
    enableUserSuccess: "User state updated successfully.",
    disableUserSuccess: "User state updated successfully.",
  },
  organization: {
    createSuccess: "Organization created successfully.",
    updateSuccess: "Organization updated successfully.",
    deleteSuccess: "Organization deleted successfully.",
    loadError: "Could not load organizations.",
  },
};

const getUsersAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  User[]
>("getUsers", (state, action) => {
  state.users = action.payload;
});

const getUserAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  User,
  { userId: string }
>("getUser", (state, action) => {
  state.users = state.users.map((u) =>
    u.id === action.payload.id ? action.payload : u,
  );
});

const getUserMeAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  User
>("getUserMe", (state, action) => {
  state.organizations = action.payload.organizations;
  state.roleGroups = action.payload.roleGroups;
});

const addUserAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  User,
  { user: User }
>("addUser", (state, action) => {
  state.users = [...state.users, action.payload];
});

const updateUserAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  User,
  { user: User }
>("updateUser", (state, action) => {
  state.users = state.users.map((c) =>
    c.id === action.payload.id ? action.payload : c,
  );
});

const deleteUserAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  unknown,
  { id: string }
>("deleteUser", (state, action) => {
  state.users = state.users.filter((c) => c.id !== action.payload);
});

const addOrganizationAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  Organization,
  { organization: Organization }
>("addOrganization", (state, action) => {
  state.organizations = [...state.organizations, action.payload.name];
});

const resetPasswordAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  unknown,
  { id: string; password: string }
>("resetPassword", (state, action) => {});

const restoreTotpAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  unknown,
  { id: string }
>("restoreTotp", (state, action) => {});

const enableUserAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  unknown,
  { id: string }
>("enableUser", (state, action) => {
  state.users = state.users.map((u) =>
    u.id === action.payload ? { ...u, enabled: true } : u,
  );
});

const disableUserAsync = createAsyncReducers<
  UserControllerState,
  UserRequestKey,
  unknown,
  { id: string }
>("disableUser", (state, action) => {
  state.users = state.users.map((u) =>
    u.id === action.payload ? { ...u, enabled: false } : u,
  );
});

const userControllerSlice = createSlice({
  name: "user-controller",
  initialState,
  reducers: {
    getUsersRequest: getUsersAsync.request,
    getUsersSuccess: getUsersAsync.success,
    getUsersFailure: getUsersAsync.failure,
    getUserRequest: getUserAsync.request,
    getUserSuccess: getUserAsync.success,
    getUserFailure: getUserAsync.failure,
    getUserMeRequest: getUserMeAsync.request,
    getUserMeSuccess: getUserMeAsync.success,
    getUserMeFailure: getUserMeAsync.failure,
    addUserRequest: addUserAsync.request,
    addUserSuccess: addUserAsync.success,
    addUserFailure: addUserAsync.failure,
    updateUserRequest: updateUserAsync.request,
    updateUserSuccess: updateUserAsync.success,
    updateUserFailure: updateUserAsync.failure,
    deleteUserRequest: deleteUserAsync.request,
    deleteUserSuccess: deleteUserAsync.success,
    deleteUserFailure: deleteUserAsync.failure,
    addOrganizationRequest: addOrganizationAsync.request,
    addOrganizationSuccess: addOrganizationAsync.success,
    addOrganizationFailure: addOrganizationAsync.failure,
    resetPasswordRequest: resetPasswordAsync.request,
    resetPasswordSuccess: resetPasswordAsync.success,
    resetPasswordFailure: resetPasswordAsync.failure,
    restoreTotpRequest: restoreTotpAsync.request,
    restoreTotpSuccess: restoreTotpAsync.success,
    restoreTotpFailure: restoreTotpAsync.failure,
    enableUserRequest: enableUserAsync.request,
    enableUserSuccess: enableUserAsync.success,
    enableUserFailure: enableUserAsync.failure,
    disableUserRequest: disableUserAsync.request,
    disableUserSuccess: disableUserAsync.success,
    disableUserFailure: disableUserAsync.failure,
  },
});

export const {
  getUserRequest,
  getUserSuccess,
  getUserFailure,
  getUserMeRequest,
  getUserMeSuccess,
  getUserMeFailure,
  getUsersRequest,
  getUsersSuccess,
  getUsersFailure,
  addUserRequest,
  addUserSuccess,
  addUserFailure,
  updateUserRequest,
  updateUserSuccess,
  updateUserFailure,
  deleteUserRequest,
  deleteUserSuccess,
  deleteUserFailure,
  addOrganizationRequest,
  addOrganizationSuccess,
  addOrganizationFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure,
  restoreTotpRequest,
  restoreTotpSuccess,
  restoreTotpFailure,
  enableUserRequest,
  enableUserSuccess,
  enableUserFailure,
  disableUserRequest,
  disableUserSuccess,
  disableUserFailure,
} = userControllerSlice.actions;

const getUserEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getUserRequest.type),
    mergeMap((action: { payload: { userId: number } }) => {
      const url = `${baseURL}/users/${action.payload.userId}`;
      return ajax.getJSON(url).pipe(
        map((response: User) => getUserSuccess(response)),
        catchError((error) => {
          return of(
            getUserFailure(
              error.message || userControllerMessages.user.userInfoError,
            ),
          );
        }),
      );
    }),
  );
};

const getUserMeEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getUserMeRequest.type),
    mergeMap(() => {
      const url = `${baseURL}/users/me`;
      return ajax.getJSON(url).pipe(
        map((response: User) => getUserMeSuccess(response)),
        catchError((error) => {
          return of(
            getUserMeFailure(
              error.message || userControllerMessages.user.userInfoError,
            ),
          );
        }),
      );
    }),
  );
};

const addOrganizationEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(addOrganizationRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          organization: Organization;
        }>,
      ) => {
        const { organization } = action.payload;
        const url = `${baseURL}/organizations`;

        return ajax({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: organization,
          responseType: "json",
        }).pipe(
          mergeMap(() =>
            of(
              addOrganizationSuccess(organization, {
                notificationOnSuccess:
                  userControllerMessages.organization.createSuccess,
              }),
            ),
          ),
          catchError((error) =>
            of(
              addUserFailure(
                error.response?.message ||
                  error.message ||
                  "Could not create organization",
              ),
            ),
          ),
        );
      },
    ),
  );

const getUsersEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(getUsersRequest.type),
    mergeMap(() =>
      ajax.getJSON(`${baseURL}/users`).pipe(
        map((users: User[]) => getUsersSuccess(users)),
        catchError((error) =>
          of(
            getUsersFailure(
              error.response?.message ||
                error.message ||
                userControllerMessages.user.usersLoadError,
            ),
          ),
        ),
      ),
    ),
  );

const addUserEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(addUserRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          user: User;
        }>,
      ) => {
        const { user } = action.payload;
        const url = `${baseURL}/users`;

        return ajax({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: user,
          responseType: "json",
        }).pipe(
          mergeMap((data: { response: User }) =>
            of(
              addUserSuccess(data.response, {
                notificationOnSuccess:
                  userControllerMessages.user.createSuccess,
              }),
            ),
          ),
          catchError((error) =>
            of(
              addUserFailure(
                error.response?.message ||
                  error.message ||
                  "Error updating configuration",
              ),
            ),
          ),
        );
      },
    ),
  );

const updateUserEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(updateUserRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          user: User;
        }>,
      ) => {
        const { user } = action.payload;
        const url = `${baseURL}/users/${user?.id}`;

        return ajax({
          url,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: user,
          responseType: "json",
        }).pipe(
          mergeMap((data: { response: User }) =>
            of(
              updateUserSuccess(data.response, {
                notificationOnSuccess:
                  userControllerMessages.user.updateSuccess,
              }),
            ),
          ),
          catchError((error) =>
            of(
              updateUserFailure(
                error.response?.message ||
                  error.message ||
                  "Error updating configuration",
              ),
            ),
          ),
        );
      },
    ),
  );

const deleteUserEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(deleteUserRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          id: number;
        }>,
      ) => {
        const { id } = action.payload;
        const url = `${baseURL}/users/${id}`;

        return ajax({ url, method: "DELETE" }).pipe(
          mergeMap(() =>
            of(
              deleteUserSuccess(id, {
                notificationOnSuccess:
                  userControllerMessages.user.deleteSuccess,
              }),
            ),
          ),
          catchError((error) =>
            of(
              deleteUserFailure(
                error.response?.message ||
                  error.message ||
                  "Error updating configuration",
              ),
            ),
          ),
        );
      },
    ),
  );

const resetPasswordEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(resetPasswordRequest.type),
    mergeMap((action: { payload: { id: string; password: string } }) =>
      ajax({
        url: `${baseURL}/users/${action.payload.id}/reset-password`,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: { password: action.payload.password },
        responseType: "json",
      }).pipe(
        mergeMap(() =>
          of(
            resetPasswordSuccess(undefined, {
              notificationOnSuccess:
                userControllerMessages.user.passwordResetSuccess,
            }),
          ),
        ),
        catchError((error) =>
          of(
            resetPasswordFailure(
              error.response?.message ||
                error.message ||
                "Error on user password reset action",
            ),
          ),
        ),
      ),
    ),
  );

const restoreTotpEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(restoreTotpRequest.type),
    mergeMap((action: { payload: { id: string } }) =>
      ajax({
        url: `${baseURL}/users/${action.payload.id}/restore-totp`,
        method: "PUT",
      }).pipe(
        mergeMap(() =>
          of(
            restoreTotpSuccess(undefined, {
              notificationOnSuccess:
                userControllerMessages.user.totpRestoreSuccess,
            }),
          ),
        ),
        catchError((error) =>
          of(
            restoreTotpFailure(
              error.response?.message ||
                error.message ||
                "Error on restore user Totp action",
            ),
          ),
        ),
      ),
    ),
  );

const enableUserEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(enableUserRequest.type),
    mergeMap((action: { payload: { id: string } }) =>
      ajax({
        url: `${baseURL}/users/${action.payload.id}/enable`,
        method: "PUT",
      }).pipe(
        mergeMap(() =>
          of(
            enableUserSuccess(action.payload.id, {
              notificationOnSuccess:
                userControllerMessages.user.enableUserSuccess,
            }),
          ),
        ),
        catchError((error) =>
          of(
            enableUserFailure(
              error.response?.message ||
                error.message ||
                "Error on user disable action",
            ),
          ),
        ),
      ),
    ),
  );

const disableUserEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(disableUserRequest.type),
    mergeMap((action: { payload: { id: string } }) =>
      ajax({
        url: `${baseURL}/users/${action.payload.id}/disable`,
        method: "PUT",
      }).pipe(
        mergeMap(() =>
          of(
            disableUserSuccess(action.payload.id, {
              notificationOnSuccess:
                userControllerMessages.user.disableUserSuccess,
            }),
          ),
        ),
        catchError((error) =>
          of(
            disableUserFailure(
              error.response?.message ||
                error.message ||
                "Error on user disable action",
            ),
          ),
        ),
      ),
    ),
  );

export default {
  actions: userControllerSlice.actions,
  reducer: userControllerSlice.reducer,
  middleware: [
    getUserEpic,
    getUserMeEpic,
    addOrganizationEpic,
    getUsersEpic,
    addUserEpic,
    updateUserEpic,
    deleteUserEpic,
    resetPasswordEpic,
    restoreTotpEpic,
    enableUserEpic,
    disableUserEpic,
  ],
  selectors: userControllerSlice.selectors,
};
